import { useEffect, useState } from 'react';
import { api, describeError, type LocationSetup, type LocationStatus } from './api.js';

const isIPhone = () => /iPhone|iPad/.test(navigator.userAgent);

function ago(iso: string): string {
  const mins = Math.round((Date.now() - Date.parse(iso)) / 60_000);
  if (mins < 2) return 'just now';
  if (mins < 90) return `${mins} minutes ago`;
  const hours = Math.round(mins / 60);
  return hours < 36 ? `${hours} hours ago` : `${Math.round(hours / 24)} days ago`;
}

/** Connects Anthony's iPhone (OwnTracks) so Alfred knows where he is and can remind him by place. */
export function LocationSettings() {
  const [status, setStatus] = useState<LocationStatus | null>(null);
  const [setup, setSetup] = useState<LocationSetup | null>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const refresh = () =>
    api
      .locationStatus()
      .then(setStatus)
      .catch(() => {});
  useEffect(() => void refresh(), []);

  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    setMessage(null);
    try {
      await fn();
      await refresh();
    } catch (err) {
      setMessage(describeError(err));
    } finally {
      setBusy(false);
    }
  };

  const copy = (text: string) =>
    void navigator.clipboard?.writeText(text).then(
      () => setMessage('Copied.'),
      () => setMessage('Couldn’t copy; select it and copy by hand.'),
    );

  if (!status) return null;
  return (
    <section>
      <h3>Location</h3>
      <p className="muted small">
        Your iPhone tells Alfred roughly where you are, for things like “remind me to grab milk when
        I’m at Woolies”. Stored encrypted, only the last 7 days kept. Not a safety system: if your
        phone stops reporting, reminders can be missed.
      </p>

      {!status.available && (
        <p className="small">Location isn’t available on this server (no encryption key set).</p>
      )}

      {status.available && status.deviceSetUp && !setup && (
        <>
          <p className="small">
            {status.lastUpdateAt
              ? `Phone connected. Last location ${ago(status.lastUpdateAt)}.`
              : 'Phone set up, but no location has arrived yet. Open OwnTracks once to send one.'}
          </p>
          <div className="row-gap">
            <button
              className="btn btn-small"
              disabled={busy}
              onClick={() => void run(async () => setSetup(await api.locationSetup()))}
            >
              Set up again
            </button>
            <button
              className="btn btn-small btn-danger-outline"
              disabled={busy}
              onClick={() =>
                void run(async () => {
                  await api.locationRemove();
                  setMessage('Turned off. Alfred will ignore that phone from now on.');
                })
              }
            >
              Turn off
            </button>
          </div>
        </>
      )}

      {status.available && !status.deviceSetUp && !setup && (
        <button
          className="btn btn-wide btn-primary"
          disabled={busy}
          onClick={() => void run(async () => setSetup(await api.locationSetup()))}
        >
          Set up my iPhone
        </button>
      )}

      {setup && (
        <ol className="setup-steps small">
          <li>
            On your iPhone, install <strong>OwnTracks</strong> from the App Store (it’s free).
          </li>
          <li>
            {isIPhone() ? (
              <>
                Tap{' '}
                <a className="btn btn-small btn-primary" href={setup.configLink}>
                  Open in OwnTracks
                </a>{' '}
                and accept the configuration.
              </>
            ) : (
              <>
                Open these Settings on your iPhone (in the Alfred app) and tap “Set up again” there
                for a one-tap link, or enter these in OwnTracks → Settings: Mode <em>HTTP</em>, and
              </>
            )}
            <dl className="setup-values">
              <dt>URL</dt>
              <dd>
                <code>{setup.url}</code>
                <button className="link-btn" onClick={() => copy(setup.url)}>
                  Copy
                </button>
              </dd>
              <dt>Authentication</dt>
              <dd>On</dd>
              <dt>User ID</dt>
              <dd>
                <code>{setup.username}</code>
              </dd>
              <dt>Password</dt>
              <dd>
                <code>{setup.password}</code>
                <button className="link-btn" onClick={() => copy(setup.password)}>
                  Copy
                </button>
              </dd>
            </dl>
          </li>
          <li>
            When iOS asks, allow location <strong>Always</strong> (and Motion &amp; Fitness). Alfred
            uses “significant changes” mode, which is light on the battery.
          </li>
          <li>
            The password is shown only now. If you lose it, tap “Set up again”.{' '}
            <button className="link-btn" onClick={() => setSetup(null)}>
              Done
            </button>
          </li>
        </ol>
      )}

      {message && <p className="small">{message}</p>}
    </section>
  );
}
